/** @format */

import React, { useEffect, useMemo, useCallback } from 'react';
import { useMeetingAttendance, useMeetingActivity, formatLogTimestampGhana } from '../hooks/useMeetingLogs';
import { useUsers } from '../hooks/useUsers';

interface MeetingActivityMetaProps {
  meetingId: string;
  showRecent?: boolean;
  maxRecent?: number;
  className?: string;
}

const MeetingActivityMeta: React.FC<MeetingActivityMetaProps> = ({
  meetingId,
  showRecent = true,
  maxRecent = 5,
  className = ''
}) => {
  const { participantIds, count: attendeeCount } = useMeetingAttendance(meetingId);
  const { logs } = useMeetingActivity(meetingId, maxRecent);
  const { fetchUsers, getUserDisplayName } = useUsers();

  // Memoize the fetch users call to prevent unnecessary API calls
  const memoizedFetchUsers = useCallback(() => {
    if (participantIds.length > 0) {
      fetchUsers(participantIds);
    }
  }, [participantIds, fetchUsers]);

  useEffect(() => {
    memoizedFetchUsers();
  }, [memoizedFetchUsers]);

  // Memoize the joined names calculation to prevent recalculation on every render
  const joinedNames = useMemo(() => {
    if (participantIds.length === 0) return '';
    
    const names = participantIds
      .slice(0, 5)
      .map((id) => getUserDisplayName(id))
      .filter(Boolean); // Filter out undefined names
    
    const remaining = Math.max(0, participantIds.length - 5);
    return names.join(', ') + (remaining > 0 ? `, +${remaining} more` : '');
  }, [participantIds, getUserDisplayName]);

  // Memoize the recent activity logs to prevent unnecessary re-renders
  const recentActivityLogs = useMemo(() => {
    if (!showRecent || logs.length === 0) return null;
    
    return logs.map((log) => (
      <li key={log.id} className="flex items-center gap-2">
        <span className="capitalize">{log.action.replace('_', ' ')}</span>
        <span>•</span>
        <span>{formatLogTimestampGhana(log.timestamp)}</span>
      </li>
    ));
  }, [showRecent, logs]);

  return (
    <div className={className}>
      <div className="mt-1 text-xs text-gray-500 flex items-center gap-3">
        <span className="inline-flex items-center px-2 py-0.5 bg-gray-100 text-gray-700 rounded-full">
          {attendeeCount} joined
        </span>
        {participantIds.length > 0 && (
          <span className="truncate" title={joinedNames}>{joinedNames}</span>
        )}
      </div>
      {showRecent && (
        <div className="mt-2 text-xs text-gray-500">
          <div className="font-medium text-gray-700 mb-1">Recent activity</div>
          <ul className="space-y-1">
            {logs.length === 0 ? (
              <li className="text-gray-400">No activity yet</li>
            ) : (
              recentActivityLogs
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default MeetingActivityMeta;


